import React, {useEffect, useRef, useState} from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useTranslation} from 'react-i18next';
import LottieView from 'lottie-react-native';
import ErrorReportingService from '../helpers/ErrorReportingService';

/**
 * Bounded auto-recovery budget.
 *
 * Switching the app module (e.g. → POS after day opening) forces a full remount
 * of the navigator (`key={navigatorKey}` in AppNavigator). React tears down the
 * entire old screen tree synchronously, and a single component throwing inside
 * its `componentWillUnmount` (notably iOS gesture/card teardown in the navigation
 * stack) crashes the commit and trips this boundary — stranding the user on the
 * error screen mid-switch.
 *
 * Because the crash happens while unmounting the OLD tree, by the time we land
 * here that tree is already gone: a single automatic reset re-mounts the fresh
 * (POS) tree cleanly. We allow a small, time-windowed budget of silent retries
 * so a genuinely persistent crash still falls back to the manual UI instead of
 * looping forever.
 */
const AUTO_RECOVERY_MAX_ATTEMPTS = 2;
const AUTO_RECOVERY_WINDOW_MS = 8000;
const AUTO_RECOVERY_DELAY_MS = 350;

const autoRecoveryTracker = {
  attempts: 0,
  windowStartedAt: 0,
};

/**
 * @param {Error} error
 * @returns {boolean} whether this error looks like a transient mount/unmount
 *   commit error that a remount is likely to clear.
 */
const isLikelyTransientCommitError = (error) => {
  const message = String(error?.message || '');

  // Hermes/JSC wordings for "called something that isn't a function" + the React
  // commit-phase teardown signatures. These are the shapes a clean remount fixes.
  return (
    /undefined is not a function/i.test(message)
    || /is not a function/i.test(message)
    || /undefined is not an object/i.test(message)
    || /componentWillUnmount/i.test(String(error?.stack || ''))
  );
};

const ErrorFallback = ({ error, resetError }) => {
  const { t } = useTranslation();
  const [autoRecovering, setAutoRecovering] = useState(false);
  const recoverTimerRef = useRef(null);

  useEffect(() => {
    ErrorReportingService.reportErrorScreenShown(error, {
      sourceComponent: 'ErrorFallback',
    });
  }, [error]);

  // Attempt silent, bounded auto-recovery for transient commit/unmount crashes
  // so a module switch never dead-ends on the error screen.
  useEffect(() => {
    const now = Date.now();

    // Reset the budget once the time window has elapsed since the first attempt.
    if (now - autoRecoveryTracker.windowStartedAt > AUTO_RECOVERY_WINDOW_MS) {
      autoRecoveryTracker.attempts = 0;
      autoRecoveryTracker.windowStartedAt = now;
    }

    const canAutoRecover =
      typeof resetError === 'function'
      && isLikelyTransientCommitError(error)
      && autoRecoveryTracker.attempts < AUTO_RECOVERY_MAX_ATTEMPTS;

    if (!canAutoRecover) {
      return undefined;
    }

    autoRecoveryTracker.attempts += 1;
    setAutoRecovering(true);

    recoverTimerRef.current = globalThis.setTimeout(() => {
      try {
        resetError();
      } catch (_) {
        // If reset itself throws, fall back to the manual UI on next render.
        setAutoRecovering(false);
      }
    }, AUTO_RECOVERY_DELAY_MS);

    return () => {
      if (recoverTimerRef.current) {
        globalThis.clearTimeout(recoverTimerRef.current);
        recoverTimerRef.current = null;
      }
    };
  }, [error, resetError]);

  // While an automatic retry is pending, show a neutral recovery screen instead
  // of the alarming error UI — the switch should feel seamless to the user.
  if (autoRecovering) {
    return (
      <LinearGradient
        colors={['#EAE5EE', '#FEE6DE', '#EBE5F2']}
        style={styles.container}
      >
        <View style={styles.content}>
          <View style={{width: '50%'}}><LottieView
            source={require('../../assets/animations/Animation - 1746448734469.json')}
            autoPlay
            loop
            resizeMode="contain"
            webStyle={styles.lottieAnimation}
            style={styles.lottieAnimation}
          /></View>
          <Text style={styles.title}>{t('loading')}</Text>
        </View>
      </LinearGradient>
    );
  }

  return (
    <LinearGradient
      colors={['#EAE5EE', '#FEE6DE', '#EBE5F2']}
      style={styles.container}
    >
      <View style={styles.content}>
        <View style={{width: '50%'}}><LottieView
          source={require('../../assets/animations/Animation - 1746448734469.json')}
          autoPlay
          loop
          resizeMode="contain"
          webStyle={styles.lottieAnimation}
          style={styles.lottieAnimation}
        /></View>

        <Text style={styles.title}>{t('error_occurred')}</Text>
        <Text style={styles.description}>{t('error_boundary_description')}</Text>

        {error && error.message && (
          <View style={styles.errorMessageContainer}>
            <Text style={styles.errorMessage}>{error.message}</Text>
          </View>
        )}

        <TouchableOpacity
          style={styles.button}
          onPress={resetError}
        >
          <Text style={styles.buttonText}>{t('try_again')}</Text>
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    width: '80%',
    alignItems: 'center',
    padding: 20,
    borderRadius: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  lottieAnimation: {
    width: 250,
    height: 250,
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
    color: '#1F2732',
  },
  description: {
    fontSize: 16,
    marginBottom: 20,
    textAlign: 'center',
    color: '#2F2F2F',
  },
  errorMessageContainer: {
    backgroundColor: 'rgba(237, 28, 36, 0.1)',
    padding: 10,
    borderRadius: 5,
    marginBottom: 20,
    width: '100%',
  },
  errorMessage: {
    color: '#ED1C24',
    textAlign: 'center',
  },
  button: {
    backgroundColor: '#f6821f',
    paddingVertical: 12,
    paddingHorizontal: 30,
    borderRadius: 25,
    marginTop: 10,
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default ErrorFallback;
